import { Component, EventEmitter, Input, Output, ViewChild, OnInit, inject } from '@angular/core';
import { FilePondOptions } from 'filepond';
import { FilePondComponent } from 'ngx-filepond';
import { Payment, PaymentOptions } from '../../core/state/order/order.model';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { FilePondConfigService } from '../../services/filepond-config.service';
import { Store } from '@ngrx/store';
import { State } from '../../core/state';
import * as UserActions from '../../core/state/users/user.actions';
import { UserProfileRequestDTO } from '../../core/state/users/models';
import { mobileNumberValidator } from '../../util/form.validators';
import { ToastService } from '@black/ui';
import { TenantBankAccount } from '../../models/invoice.model';

@Component({
  selector: 'black-billing-form',
  templateUrl: './billing-form.component.html',
  styleUrl: './billing-form.component.scss',
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class BillingFormComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private store = inject<Store<State>>(Store);
  private toast = inject(ToastService);
  private filePondConfig = inject(FilePondConfigService);

  private _bankAccounts: TenantBankAccount[] = [];
  selectedBankAccount: TenantBankAccount | null = null;

  @Input() amount?: number;
  @Input() currency = 'LKR';
  @Input() referenceNumber?: string;

  @Input() set bankAccounts(accounts: TenantBankAccount[] | undefined) {
    this._bankAccounts = (accounts || []).filter(
      (a) => a.bankName?.trim() && a.accountNumber?.trim()
    );
    if (this._bankAccounts.length > 0) {
      const current = this.paymentOptionForm.controls.bank.value;
      const found = this._bankAccounts.find(
        (a) => `${a.bankName}[${a.accountNumber}]` === current
      );
      const selected = found || this._bankAccounts[0];
      this.selectedBankAccount = selected;
      this.paymentOptionForm.controls.bank.setValue(
        `${selected.bankName}[${selected.accountNumber}]`
      );
    } else {
      this.selectedBankAccount = null;
      this.paymentOptionForm.controls.bank.setValue('');
    }
  }
  get bankAccounts(): TenantBankAccount[] {
    return this._bankAccounts;
  }

  onSelectBankAccount(account: TenantBankAccount) {
    this.selectedBankAccount = account;
    this.paymentOptionForm.controls.bank.setValue(
      `${account.bankName}[${account.accountNumber}]`
    );
  }

  selectedPaymentOption: 'BANK_TRANSFER' | 'PAYHERE' | 'CARD' = 'BANK_TRANSFER';
  @ViewChild('myPond')
  myPond!: FilePondComponent;

  @Output()
  successPayment = new EventEmitter();

  pondOptions: FilePondOptions = {
    allowMultiple: false,
    labelIdle: 'Drop files here or Browse',
    instantUpload: true,
    allowFileTypeValidation: true,
    acceptedFileTypes: [
      'image/png',
      'image/jpeg',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
  };

  billingForm = this.fb.group({
    name: ['', Validators.required],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    city: ['', Validators.required],
    province: [''],
    postal: [''],
    contact: ['', [Validators.required, mobileNumberValidator]],
    country: ['', Validators.required],
    updateProfile: [false],
  });

  paymentOptionForm = this.fb.group({
    bank: ['', Validators.required],
    slip: ['', Validators.required],
  });

  async ngOnInit(): Promise<void> {
    const server = await this.filePondConfig.getServerConfig({ allowRevert: true });
    this.pondOptions = { ...this.pondOptions, server };
    this.loadBillingInformation();
  }

  loadBillingInformation() {
    this.store
      .select((state) => state.users.user)
      .subscribe((user) => {
        if (user && user.profile) {
          this.billingForm.patchValue({
            name: `${user.profile.personal.firstName} ${user.profile.personal.lastName}`,
            addressLine1: user.profile.address?.addressLine1 || '',
            addressLine2: user.profile.address?.addressLine2 || '',
            city: user.profile.address?.city || '',
            province: user.profile.address?.province || '',
            postal: user.profile.address?.postalCode || '',
            contact: user.profile.personal.mobile || '',
            country: user.profile.address?.country || '',
            updateProfile: false,
          });
        }
      });
  }

  selectedPaymentOptionChange(option: 'BANK_TRANSFER' | 'PAYHERE' | 'CARD') {
    this.selectedPaymentOption = option;
  }

  processPayment() {
    console.log('selectedPaymentOption', this.selectedPaymentOption);

    if (this.selectedPaymentOption === 'PAYHERE') {
      this.billingForm.markAllAsTouched();
      if (!this.billingForm.valid) {
        this.toast.showToast('Form is invalid!', 'error');
        return;
      }
    } else if (this.selectedPaymentOption === 'BANK_TRANSFER') {
      if (!this.selectedBankAccount) {
        this.toast.showToast(
          'Bank transfer is currently unavailable. Please contact the academy administrator.',
          'error'
        );
        return;
      }
      this.paymentOptionForm.markAllAsTouched();
      if (!this.paymentOptionForm.valid) {
        this.toast.showToast('Form is invalid!', 'error');
        return;
      }
    }

    const billingData = this.billingForm.value;
    const bankData = this.paymentOptionForm.value;

    const payment: Payment = {
      billingInformation:
        this.selectedPaymentOption === 'PAYHERE'
          ? {
              name: billingData.name ?? '',
              addressLine1: billingData.addressLine1 ?? '',
              addressLine2: billingData.addressLine2 ?? '',
              city: billingData.city ?? '',
              province: billingData.province ?? '',
              postalCode: billingData.postal ?? '',
              contactNumber: billingData.contact ?? '',
              country: billingData.country ?? '',
            }
          : undefined,
      modeOfPayment:
        this.selectedPaymentOption === 'PAYHERE'
          ? PaymentOptions.PAYHERE
          : PaymentOptions.BANK_TRANSFER,
      amount: this.amount,
      currencyCode: this.currency,
      bankData:
        this.selectedPaymentOption === 'BANK_TRANSFER'
          ? {
              merchant: bankData.bank,
              slip: bankData.slip,
            }
          : undefined,
    };

    // If updateProfile is checked, update the user profile
    if (this.selectedPaymentOption === 'PAYHERE' && billingData.updateProfile) {
      const name = billingData.name ?? '';
      const [firstName, ...lastNameParts] = name.split(' ');
      const lastName = lastNameParts.join(' ');

      const profileUpdate: UserProfileRequestDTO = {
        firstName,
        lastName,
        mobile: billingData.contact,
        addressLine1: billingData.addressLine1,
        addressLine2: billingData.addressLine2,
        city: billingData.city,
        province: billingData.province,
        postalCode: billingData.postal,
        country: billingData.country,
      };

      this.store.dispatch(
        UserActions.updateUserProfile({
          userProfile: profileUpdate,
          redirectOnSuccess: false,
        })
      );
    }

    this.successPayment.emit(payment);
  }

  pondHandleInit() {
    console.log('FilePond has initialised');
  }

  pondHandleAddFile(event: any) {
    console.log('A file was added', event);
  }

  pondHandleProcessFile(event: any) {
    if (!event.error) {
      this.paymentOptionForm.controls.slip.setValue(event.file.serverId);
    }
  }
}
